import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import { storeApi, formatStoreError } from "@/lib/storefront";
import { toast } from "sonner";

export default function StoreFooter() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSending(true);
    try {
      const { data } = await storeApi.post("/newsletter", { email });
      toast.success(data.message || "Subscribed");
      setEmail("");
    } catch (err) {
      toast.error(formatStoreError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="border-t border-[#DDD6C5] bg-[#E8E2D2]" data-testid="store-footer">
      {/* Newsletter */}
      <div className="border-b border-[#DDD6C5] py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="mb-2 text-xs uppercase tracking-[0.3em] text-[#2D2823]">Quiet updates, never spam</div>
          <h3 className="font-display text-3xl text-[#1A1A1A] md:text-5xl">
            Join our <span className="font-display-soft">circle</span>
          </h3>
          <p className="mt-3 text-sm text-[#2D2823] md:text-base">
            New arrivals, seasonal edits and small notes about beautiful things — one email at a time.
          </p>
          <form onSubmit={subscribe} className="mx-auto mt-8 flex max-w-md items-center gap-2 rounded-full border border-[#DDD6C5] bg-white p-1" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent px-4 py-2 text-sm text-[#1A1A1A] outline-none placeholder:text-[#2D2823]/50"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              disabled={sending}
              className="rounded-full bg-[#1A1A1A] px-6 py-2 text-sm text-white transition-colors hover:bg-[#7D8471] disabled:opacity-50"
              data-testid="newsletter-submit"
            >
              {sending ? "…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      {/* Columns */}
      <div className="mx-auto grid max-w-[1440px] gap-10 px-6 py-14 md:grid-cols-4 md:px-10">
        <div>
          <div className="font-display text-2xl">Luna. Creative Home</div>
          <p className="mt-3 text-base leading-relaxed text-[#2D2823]">
            A home where creativity grows. Thoughtfully curated craft kits, quietly beautiful decor, and storage that makes family life feel lighter. Shipped across the US.
          </p>
          <div className="mt-4 flex gap-3 text-[#2D2823]">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#1A1A1A]" data-testid="social-instagram"><Instagram size={18} /></a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[#1A1A1A]" data-testid="social-facebook"><Facebook size={18} /></a>
          </div>
        </div>
        <div>
          <Link to="/catalog" className="mb-4 block text-xs uppercase tracking-[0.22em] text-[#2D2823] hover:text-[#1A1A1A]" data-testid="footer-shop-heading">Shop</Link>
          <ul className="space-y-3 text-base">
            <li><Link to="/catalog" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-shop-all">All products</Link></li>
            <li><Link to="/catalog?sort=-created_at" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-shop-new">New arrivals</Link></li>
            <li><Link to="/catalog?on_sale=true" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-shop-sale">On sale</Link></li>
            <li><Link to="/brands-list" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-shop-brands">Brands</Link></li>
          </ul>
        </div>
        <div>
          <Link to="/faq" className="mb-4 block text-xs uppercase tracking-[0.22em] text-[#2D2823] hover:text-[#1A1A1A]" data-testid="footer-help-heading">Help</Link>
          <ul className="space-y-3 text-base">
            <li><Link to="/pages/delivery" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-help-delivery">Delivery</Link></li>
            <li><Link to="/pages/returns" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-help-returns">Returns</Link></li>
            <li><Link to="/contact" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-help-contact">Contact</Link></li>
            <li><Link to="/faq" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-help-faq">FAQ</Link></li>
            <li><Link to="/pages/privacy" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-help-privacy">Privacy</Link></li>
            <li><Link to="/pages/terms" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-help-terms">Terms</Link></li>
          </ul>
        </div>
        <div>
          <Link to="/pages/our-story" className="mb-4 block text-xs uppercase tracking-[0.22em] text-[#2D2823] hover:text-[#1A1A1A]" data-testid="footer-about-heading">About</Link>
          <ul className="space-y-3 text-base">
            <li><Link to="/pages/our-story" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-about-story">Our story</Link></li>
            <li><Link to="/contact" className="text-[#1A1A1A] hover:text-[#7D8471]" data-testid="footer-about-contact">Contact</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#DDD6C5] py-5">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-2 px-6 text-sm text-[#2D2823] md:px-10">
          <div>© {new Date().getFullYear()} Brightlane LLC · Luna. Creative Home · All rights reserved.</div>
          <div className="font-mono text-xs uppercase tracking-[0.25em]">30 N Gould St, Ste 89712 · Sheridan, WY 82801</div>
        </div>
      </div>
    </footer>
  );
}
